/**
 * #1321 — the single source of truth for whether an affiliate's Company & Bank
 * Details are complete.
 *
 * WHY THIS FILE EXISTS. Company and bank data live in two places for
 * historical reasons:
 *
 *   Company  canonical: partner_service.affiliate_billing
 *            onboarding writes: affiliates.company_{name,type,registration_number,address}
 *   Bank     canonical: partner_service.bank_accounts
 *            onboarding writes: affiliates.bank_account_details (JSONB)
 *
 * AffiliateDetailsPage has read both sides since #839. The global warning
 * banner did not — it read only the canonical stores, so every affiliate whose
 * details arrived through onboarding was told they were missing data the page
 * was displaying on the same screen. On staging that was 0 of 103 affiliates
 * with an affiliate_billing row and 0 with a bank_accounts row, against 91
 * carrying a complete legacy bank record.
 *
 * Both consumers now call these helpers, so the banner and the page cannot
 * disagree again. The mandatory-field sets below are the page's own
 * save-time required sets — per #1321 the trigger is defined as the fields
 * /affiliate-details treats as mandatory.
 */
import type { AffiliateBilling, AffiliateDetails } from '@/services/api/affiliate-details.api'

/** Company fields that must be present. Mirrors the page's save-time check. */
export const COMPANY_REQUIRED_FIELDS = [
  'companyName',
  'companyType',
  'companyRegistrationNumber',
  'companyAddress',
] as const

/** Bank fields that must be present. iban and swift are deliberately optional. */
export const BANK_REQUIRED_FIELDS = [
  'accountHolder',
  'bankName',
  'bankNumber',
  'branchNumber',
] as const

export interface ResolvedCompanyDetails {
  companyName: string
  companyType: string
  companyRegistrationNumber: string
  companyAddress: string
}

export interface ResolvedBankDetails {
  /** Set only when the data came from a real bank_accounts row (tier 1). */
  accountId: string | null
  accountHolder: string
  bankName: string
  /** Israeli Bank Number (מספר בנק). */
  bankNumber: string
  /** Branch (מספר סניף). */
  branchNumber: string
  /** Last-4 mask. The plaintext number is never sent to the client. */
  accountNumberMasked: string
  iban: string
  swift: string
}

export interface AffiliateCompletenessInput {
  /** GET /affiliates/:id/billing — null when the affiliate has no billing row. */
  billing?: AffiliateBilling | null
  /** GET /affiliates/:id — carries the onboarding-era fallbacks. */
  details?: AffiliateDetails | null
  /** GET /bank-accounts — scoped to the session's own affiliate. */
  bankAccounts?: unknown[] | null
}

export interface AffiliateCompleteness {
  companyMissing: boolean
  bankMissing: boolean
  /** True when the warning banner should be shown. */
  isIncomplete: boolean
}

const str = (value: unknown): string => (value == null ? '' : String(value).trim())

/**
 * Company details, canonical store first, onboarding-era affiliate record
 * second (#839). A billing row that exists but has a blank field does NOT fall
 * through to the affiliate record: a saved blank is a deliberate blank, and
 * silently backfilling it would hide the very gap the banner exists to report.
 */
export function resolveCompanyDetails(
  billing?: AffiliateBilling | null,
  details?: AffiliateDetails | null,
): ResolvedCompanyDetails {
  const src: Record<string, unknown> = (billing ?? details ?? {}) as Record<string, unknown>
  return {
    companyName: str(src.companyName),
    companyType: str(src.companyType),
    companyRegistrationNumber: str(src.companyRegistrationNumber),
    companyAddress: str(src.companyAddress),
  }
}

/**
 * Bank details in the same three-tier priority order the page hydrates from:
 *
 *   1. the viewer's own bank_accounts rows (source of truth when present),
 *   2. AffiliateDetails.bankAccount — the structured summary, which an admin
 *      viewing ANOTHER affiliate can see (the /bank-accounts endpoint is
 *      session-scoped and would return the wrong affiliate's data),
 *   3. AffiliateDetails.bankDetails — the legacy snake_case JSONB.
 *
 * Returns null when the affiliate has no bank record in any tier.
 *
 * Tier 1 reads bank/branch from `metadata` (where the page writes them, there
 * being no dedicated columns) but also accepts top-level equivalents, so a row
 * created outside this UI is not misreported as missing a branch number.
 */
export function resolveBankDetails(
  bankAccounts?: unknown[] | null,
  details?: AffiliateDetails | null,
): ResolvedBankDetails | null {
  const list = Array.isArray(bankAccounts) ? (bankAccounts as Record<string, any>[]) : []
  const primary = list.find((a) => a?.isDefault || a?.is_primary || a?.is_default) || list[0]

  if (primary) {
    const meta = primary.metadata || {}
    return {
      accountId: primary.id ? String(primary.id) : null,
      accountHolder: str(
        primary.accountHolderName || primary.account_holder || primary.account_holder_name,
      ),
      bankName: str(primary.bankName || primary.bank_name),
      bankNumber: str(meta.bankNumber || primary.bankCode || primary.bank_code),
      branchNumber: str(meta.branchNumber || primary.branchNumber || primary.branch_number),
      accountNumberMasked: str(primary.accountNumberMasked || primary.account_number_masked),
      iban: str(primary.iban),
      swift: str(primary.swiftCode || primary.swift_code),
    }
  }

  const summary = (details as Record<string, any> | null | undefined)?.bankAccount
  if (summary && typeof summary === 'object') {
    const resolved: ResolvedBankDetails = {
      accountId: null,
      accountHolder: str(summary.accountHolderName),
      bankName: str(summary.bankName),
      bankNumber: str(summary.bankCode),
      branchNumber: str(summary.branchNumber),
      accountNumberMasked: str(summary.accountNumberMasked),
      iban: str(summary.iban),
      swift: str(summary.swiftCode),
    }
    if (hasAnyValue(resolved)) return resolved
  }

  const legacy = (details as Record<string, any> | null | undefined)?.bankDetails
  if (legacy && typeof legacy === 'object') {
    const resolved: ResolvedBankDetails = {
      accountId: null,
      accountHolder: str(legacy.account_holder || legacy.accountHolder),
      bankName: str(legacy.bank_name || legacy.bankName),
      bankNumber: str(legacy.bank_code || legacy.bankCode),
      branchNumber: str(legacy.branch_code || legacy.branchCode),
      accountNumberMasked: str(legacy.accountNumberMasked),
      iban: str(legacy.iban),
      swift: str(legacy.swift_code || legacy.swiftCode || legacy.swift),
    }
    if (hasAnyValue(resolved)) return resolved
  }

  return null
}

/** True when a resolved record carries at least one non-empty value. */
function hasAnyValue(bank: ResolvedBankDetails): boolean {
  return (
    !!bank.accountHolder ||
    !!bank.bankName ||
    !!bank.bankNumber ||
    !!bank.branchNumber ||
    !!bank.accountNumberMasked ||
    !!bank.iban ||
    !!bank.swift
  )
}

/**
 * Evaluate both halves. `bankMissing` is true when the affiliate has no bank
 * record at all, when a mandatory field is blank, or when no account number is
 * on file — a bank_accounts row proves one exists even without a mask, since
 * the number is stored encrypted and only ever returned masked.
 */
export function evaluateAffiliateCompleteness(
  input: AffiliateCompletenessInput,
): AffiliateCompleteness {
  const company = resolveCompanyDetails(input.billing, input.details)
  const companyMissing = COMPANY_REQUIRED_FIELDS.some((field) => !company[field])

  const bank = resolveBankDetails(input.bankAccounts, input.details)
  const bankMissing =
    !bank ||
    BANK_REQUIRED_FIELDS.some((field) => !bank[field]) ||
    (!bank.accountId && !bank.accountNumberMasked)

  return { companyMissing, bankMissing, isIncomplete: companyMissing || bankMissing }
}
